/**
 * Vector Search Module
 * Handles vector database interface and AI-powered device search
 *
 * Types aligned with vector_db canister declarations (vector_db.did.d.ts)
 */


// ============================================================================
// Type Definitions - Matching vector_db.did.d.ts
// ============================================================================

/**
 * Quick search criteria structure
 */
interface QuickSearchCriteria {
    device_type?: string;
    os_family?: string;
    cloud_provider?: string;
}

/**
 * Quick search button configuration
 */
interface QuickSearchButton {
    label: string;
    criteria: QuickSearchCriteria;
}

/**
 * Device type with count for statistics
 */
interface DeviceTypeCount {
    type: string;
    count: number;
}

/**
 * Metadata tuple format [key, value] - matches canister Array<[string, string]>
 */
type MetadataTuple = [string, string];

/**
 * Database stats tuple - canister returns Array<[string, number]> (nat32)
 */
type StatsTuple = [string, number];

/**
 * SimilarityResult from canister (vector_db.did.d.ts line 460)
 * { metadata: Array<[string, string]>, device_id: DeviceId, similarity_score: number }
 */
interface SimilarityResult {
    metadata: MetadataTuple[];
    device_id: string;
    similarity_score: number;
}

/**
 * QueryResult from canister (vector_db.did.d.ts line 368)
 * { query_time_ms: bigint, total_searched: number, results: Array<SimilarityResult> }
 */
interface QueryResult {
    query_time_ms: bigint | number;
    total_searched: number;
    results: SimilarityResult[];
}

/**
 * Device tuple format [device_id, metadata[]] - matches canister return
 */
type DeviceTuple = [string, MetadataTuple[]];

/**
 * CanisterClient interface (minimal for type safety)
 */
interface CanisterClient {
    callCanister<T>(canisterName: string, method: string, args: any[]): Promise<T>;
}

// ============================================================================
// VectorSearch Class
// ============================================================================

/**
 * Main class for vector database UI and search operations
 * Provides similarity search, filtering, and browsing functionality
 */
export class VectorSearch {
    private canisterClient: CanisterClient | null;
    private knownDeviceTypes: DeviceTypeCount[] = [];

    constructor() {
        this.canisterClient = null;
    }

    /**
     * Set module dependencies (dependency injection)
     */
    setDependencies(canisterClient: CanisterClient, _logger: unknown): void {
        this.canisterClient = canisterClient;
    }

    /**
     * Convert metadata tuples to a plain object for easy access
     */
    private metadataToObject(metadata: MetadataTuple[]): Record<string, string> {
        const obj: Record<string, string> = {};
        for (const [key, value] of metadata) {
            obj[key] = value;
        }
        return obj;
    }

    /**
     * Initialize the Vector Search page
     */
    async initializePage(): Promise<void> {
        try {
            console.log('🔍 Initializing Vector Search page...');

            // Setup search form FIRST (synchronous) to prevent default form GET submission
            this.setupSearchForm();

            // Load database stats (async canister call)
            await this.loadDatabaseStats();

            // Load recent devices
            await this.loadRecentDevices();

            console.log('✅ Vector Search page initialized');
        } catch (error) {
            console.error('❌ Failed to initialize Vector Search page:', error);
            this.showError('Failed to initialize Vector Search');
        }
    }

    /**
     * Load and display vector database statistics
     * Canister returns: Array<[string, number]> (text → nat32 pairs)
     */
    async loadDatabaseStats(): Promise<void> {
        if (!this.canisterClient) {
            console.warn('VectorSearch: canisterClient not available for stats');
            return;
        }

        try {
            console.log('📊 Loading database stats...');
            const stats = await this.canisterClient.callCanister<StatsTuple[]>(
                'vector_db',
                'get_database_stats',
                []
            );
            console.log('📊 Database stats received:', stats);
            this.displayDatabaseStats(stats);
        } catch (error) {
            console.error('Failed to load database stats:', error);
        }
    }

    /**
     * Display database statistics in the UI
     * Stats is Array<[string, number]> e.g. [["total_embeddings", 8], ["type_Server", 5]]
     */
    private displayDatabaseStats(stats: StatsTuple[] | null): void {
        const statsContainer = document.getElementById('vector-db-stats');
        if (!statsContainer || !stats) return;

        // Build map from [string, number] tuples
        const statsMap = new Map<string, number>();
        for (const [key, value] of stats) {
            statsMap.set(key, Number(value));
        }

        const totalEmbeddings = statsMap.get('total_embeddings') || 0;

        // Extract device type counts and store for dynamic UI population
        const deviceTypes: DeviceTypeCount[] = [];
        for (const [key, value] of statsMap) {
            if (key.startsWith('type_')) {
                const type = key.replace('type_', '');
                deviceTypes.push({ type, count: value });
            }
        }
        this.knownDeviceTypes = deviceTypes;
        this.populateDeviceTypeDropdowns(deviceTypes);

        let html = `
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 class="text-lg font-semibold text-blue-900 mb-4">Vector Database Statistics</h3>
                <div class="grid grid-cols-2 gap-4">
                    <div class="text-center">
                        <div class="text-3xl font-bold text-blue-600">${totalEmbeddings}</div>
                        <div class="text-sm text-blue-700">Total Embeddings</div>
                    </div>
                    <div class="text-center">
                        <div class="text-3xl font-bold text-blue-600">${deviceTypes.length}</div>
                        <div class="text-sm text-blue-700">Device Types</div>
                    </div>
                </div>
                ${deviceTypes.length > 0 ? `
                <div class="mt-4">
                    <h4 class="text-sm font-medium text-blue-900 mb-2">Device Type Distribution</h4>
                    <div class="space-y-2">
                        ${deviceTypes.map(dt => `
                            <div class="flex justify-between items-center text-sm">
                                <span class="text-blue-700 capitalize">${dt.type}</span>
                                <span class="bg-blue-100 text-blue-800 px-2 py-1 rounded">${dt.count}</span>
                            </div>
                        `).join('')}
                    </div>
                </div>
                ` : ''}
            </div>
        `;

        statsContainer.innerHTML = html;
    }

    /**
     * Populate device type dropdowns from actual database stats
     */
    private populateDeviceTypeDropdowns(deviceTypes: DeviceTypeCount[]): void {
        const dropdownIds = ['similarity-device-type', 'filter-device-type'];
        for (const id of dropdownIds) {
            const select = document.getElementById(id) as HTMLSelectElement | null;
            if (!select) continue;

            // Keep the first default option, remove the rest
            const defaultOption = select.options[0];
            select.innerHTML = '';
            select.appendChild(defaultOption);

            // Add options from actual data
            for (const dt of deviceTypes) {
                const option = document.createElement('option');
                option.value = dt.type;
                option.textContent = `${dt.type} (${dt.count})`;
                select.appendChild(option);
            }
        }

        // Also regenerate quick search buttons with actual types
        this.setupQuickSearchButtons();
    }

    /**
     * Setup search form event handlers
     */
    private setupSearchForm(): void {
        const searchForm = document.getElementById('vector-search-form') as HTMLFormElement | null;
        if (searchForm) {
            searchForm.addEventListener('submit', async (e) => {
                e.preventDefault();
                await this.performSearch();
            });
        }

        // Setup search type tabs
        const searchTabs = document.querySelectorAll<HTMLElement>('[data-search-tab]');
        searchTabs.forEach(tab => {
            tab.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                if (target.dataset.searchTab) {
                    this.switchSearchTab(target.dataset.searchTab);
                }
            });
        });

        // Setup quick search buttons
        this.setupQuickSearchButtons();
    }

    /**
     * Setup quick search buttons for common queries
     */
    private setupQuickSearchButtons(): void {
        // Generate quick searches dynamically from known device types
        const quickSearches: QuickSearchButton[] = [];
        for (const dt of this.knownDeviceTypes) {
            quickSearches.push({ label: `All ${dt.type}`, criteria: { device_type: dt.type } });
            // Add OS-family variants for Server type
            if (dt.type === 'Server') {
                quickSearches.push({ label: 'Linux Servers', criteria: { device_type: 'Server', os_family: 'Linux' } });
                quickSearches.push({ label: 'Windows Servers', criteria: { device_type: 'Server', os_family: 'Windows' } });
            }
        }

        const quickSearchContainer = document.getElementById('quick-search-buttons');
        if (!quickSearchContainer) return;

        const buttonsHtml = quickSearches.map(search => `
            <button type="button" class="quick-search-btn bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                    data-criteria='${JSON.stringify(search.criteria)}'>
                ${search.label}
            </button>
        `).join('');

        quickSearchContainer.innerHTML = buttonsHtml;

        // Add event listeners
        quickSearchContainer.querySelectorAll<HTMLButtonElement>('.quick-search-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const target = e.target as HTMLButtonElement;
                const criteria = JSON.parse(target.dataset.criteria || '{}') as QuickSearchCriteria;
                await this.performQuickSearch(criteria);
            });
        });
    }

    /**
     * Switch between search tabs (similarity, filter, browse)
     */
    private switchSearchTab(tabName: string): void {
        // Update tab appearance
        document.querySelectorAll<HTMLElement>('[data-search-tab]').forEach(tab => {
            tab.classList.remove('bg-blue-500', 'text-white');
            tab.classList.add('bg-gray-200', 'text-gray-700');
        });

        const activeTab = document.querySelector<HTMLElement>(`[data-search-tab="${tabName}"]`);
        if (activeTab) {
            activeTab.classList.remove('bg-gray-200', 'text-gray-700');
            activeTab.classList.add('bg-blue-500', 'text-white');
        }

        // Show/hide search forms
        document.querySelectorAll<HTMLElement>('[data-search-form]').forEach(form => {
            form.classList.add('hidden');
        });

        const activeForm = document.querySelector<HTMLElement>(`[data-search-form="${tabName}"]`);
        if (activeForm) {
            activeForm.classList.remove('hidden');
        }
    }

    /**
     * Perform search based on active tab (public for onclick handler)
     */
    async performSearch(): Promise<void> {
        const activeTab = document.querySelector<HTMLElement>('[data-search-tab].bg-blue-500');
        if (!activeTab) {
            console.warn('VectorSearch: No active search tab found');
            return;
        }

        const searchType = activeTab.dataset.searchTab;
        console.log('🔍 Performing search, type:', searchType);

        switch (searchType) {
            case 'similarity':
                await this.performSimilaritySearch();
                break;
            case 'filter':
                await this.performFilterSearch();
                break;
            case 'browse':
                await this.performBrowseSearch();
                break;
        }
    }

    /**
     * Perform similarity search based on form criteria
     */
    private async performSimilaritySearch(): Promise<void> {
        const formContainer = document.querySelector<HTMLElement>('[data-search-form="similarity"]');
        if (!formContainer) return;

        const criteria: Record<string, string> = {};
        const inputs = formContainer.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input[name], select[name]');
        for (const input of inputs) {
            const value = input.value.trim();
            if (value) {
                criteria[input.name] = value;
            }
        }

        const limitInput = document.getElementById('similarity-limit') as HTMLInputElement | null;
        const limit = parseInt(limitInput?.value || '10') || 10;

        if (Object.keys(criteria).length === 0) {
            this.showError('Please enter at least one search criterion');
            return;
        }

        await this.performVectorSearch(criteria, limit);
    }

    /**
     * Perform quick search with predefined criteria
     */
    private async performQuickSearch(criteria: QuickSearchCriteria): Promise<void> {
        await this.performVectorSearch(criteria as Record<string, string>, 10);
    }

    /**
     * Execute vector similarity search
     * Canister: similarity_search(Array<[string, string]>, number) -> QueryResult
     */
    private async performVectorSearch(
        criteria: Record<string, string>,
        limit: number = 10
    ): Promise<void> {
        if (!this.canisterClient) {
            this.showError('Canister client not available');
            return;
        }

        this.showSearching();

        try {
            console.log('🔍 Performing vector search:', criteria);

            // Convert criteria to Array<[string, string]> tuples as expected by canister
            const searchCriteria: MetadataTuple[] = Object.entries(criteria);

            const result = await this.canisterClient.callCanister<QueryResult>(
                'vector_db',
                'similarity_search',
                [searchCriteria, limit]
            );

            console.log('🔍 Search result:', result);
            this.displaySearchResults(result, criteria);

        } catch (error) {
            console.error('Vector search failed:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            this.showError(`Search failed: ${errorMessage}`);
        }
    }

    /**
     * Perform filter search by device type
     * Canister: get_devices_by_type(string) -> Array<[DeviceId, Array<[string, string]>]>
     */
    private async performFilterSearch(): Promise<void> {
        const deviceTypeSelect = document.getElementById('filter-device-type') as HTMLSelectElement | null;
        const deviceType = deviceTypeSelect?.value;

        if (!deviceType) {
            this.showError('Please select a device type');
            return;
        }

        this.showSearching();

        try {
            const result = await this.canisterClient!.callCanister<DeviceTuple[]>(
                'vector_db',
                'get_devices_by_type',
                [deviceType]
            );

            console.log('🔍 Filter result:', result);
            this.displayFilterResults(result, deviceType);

        } catch (error) {
            console.error('Filter search failed:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            this.showError(`Filter search failed: ${errorMessage}`);
        }
    }

    /**
     * Browse all devices in vector database
     * Canister: get_all_devices() -> Array<[DeviceId, Array<[string, string]>]>
     */
    private async performBrowseSearch(): Promise<void> {
        this.showSearching();

        try {
            const result = await this.canisterClient!.callCanister<DeviceTuple[]>(
                'vector_db',
                'get_all_devices',
                []
            );

            console.log('🔍 Browse result:', result);
            this.displayBrowseResults(result);

        } catch (error) {
            console.error('Browse failed:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            this.showError(`Browse failed: ${errorMessage}`);
        }
    }

    /**
     * Display similarity search results
     * QueryResult: { query_time_ms: bigint, total_searched: number, results: SimilarityResult[] }
     * SimilarityResult: { metadata: Array<[string, string]>, device_id: string, similarity_score: number }
     */
    private displaySearchResults(
        result: QueryResult,
        criteria: Record<string, string>
    ): void {
        const resultsContainer = document.getElementById('vector-search-results');
        if (!resultsContainer) return;

        const queryTime = Number(result.query_time_ms || 0);
        const totalSearched = result.total_searched || 0;
        const results = result.results || [];

        let html = `
            <div class="bg-white border border-gray-200 rounded-lg p-6">
                <div class="flex justify-between items-center mb-4">
                    <h3 class="text-lg font-semibold text-gray-900">Similarity Search Results</h3>
                    <div class="text-sm text-gray-500">
                        ${results.length} results in ${queryTime}ms (searched ${totalSearched} devices)
                    </div>
                </div>

                <div class="mb-4 text-sm text-gray-600">
                    <strong>Search criteria:</strong> ${JSON.stringify(criteria)}
                </div>
        `;

        if (results.length === 0) {
            html += `
                <div class="text-center py-8 text-gray-500">
                    <p>No similar devices found</p>
                </div>
            `;
        } else {
            html += `<div class="space-y-4">`;

            results.forEach((item: SimilarityResult) => {
                const similarityPercentage = Math.round(item.similarity_score * 100);
                // metadata is Array<[string, string]> tuples - convert to object
                const meta = this.metadataToObject(item.metadata);

                html += `
                    <div class="border border-gray-200 rounded-lg p-4">
                        <div class="flex justify-between items-start mb-3">
                            <h4 class="font-medium text-gray-900">${item.device_id}</h4>
                            <div class="flex items-center">
                                <div class="text-sm font-medium text-blue-600">${similarityPercentage}% match</div>
                                <div class="ml-2 w-16 bg-gray-200 rounded-full h-2">
                                    <div class="bg-blue-600 h-2 rounded-full" style="width: ${similarityPercentage}%"></div>
                                </div>
                            </div>
                        </div>
                        <div class="grid grid-cols-2 gap-4 text-sm">
                `;

                const keyFields = ['device_type', 'ip', 'ip_address', 'hostname', 'os', 'os_family', 'status'];
                keyFields.forEach(field => {
                    const value = meta[field];
                    if (value) {
                        html += `
                            <div>
                                <span class="text-gray-600">${field.replace(/_/g, ' ')}:</span>
                                <span class="font-medium ml-1">${value}</span>
                            </div>
                        `;
                    }
                });

                html += `
                        </div>
                    </div>
                `;
            });

            html += `</div>`;
        }

        html += `</div>`;
        resultsContainer.innerHTML = html;
    }

    /**
     * Display filter results by device type
     */
    private displayFilterResults(result: DeviceTuple[], deviceType: string): void {
        const resultsContainer = document.getElementById('vector-search-results');
        if (!resultsContainer) return;

        let html = `
            <div class="bg-white border border-gray-200 rounded-lg p-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-4">
                    ${deviceType.charAt(0).toUpperCase() + deviceType.slice(1)} Devices (${result.length})
                </h3>
        `;

        if (result.length === 0) {
            html += `
                <div class="text-center py-8 text-gray-500">
                    <p>No ${deviceType} devices found</p>
                </div>
            `;
        } else {
            html += this.renderDeviceList(result);
        }

        html += `</div>`;
        resultsContainer.innerHTML = html;
    }

    /**
     * Display browse results (all devices)
     */
    private displayBrowseResults(result: DeviceTuple[]): void {
        const resultsContainer = document.getElementById('vector-search-results');
        if (!resultsContainer) return;

        let html = `
            <div class="bg-white border border-gray-200 rounded-lg p-6">
                <h3 class="text-lg font-semibold text-gray-900 mb-4">
                    All Devices (${result.length})
                </h3>
        `;

        if (result.length === 0) {
            html += `
                <div class="text-center py-8 text-gray-500">
                    <p>No devices found in vector database</p>
                </div>
            `;
        } else {
            html += this.renderDeviceList(result);
        }

        html += `</div>`;
        resultsContainer.innerHTML = html;
    }

    /**
     * Render a list of devices as HTML
     * Each device is [deviceId, Array<[string, string]>]
     */
    private renderDeviceList(devices: DeviceTuple[]): string {
        let html = `<div class="grid grid-cols-1 md:grid-cols-2 gap-4">`;

        devices.forEach(([deviceId, metadata]) => {
            const meta = this.metadataToObject(metadata);
            const deviceType = meta['device_type'] || 'unknown';
            const ipAddress = meta['ip'] || meta['ip_address'] || 'N/A';
            const hostname = meta['hostname'] || deviceId;
            const os = meta['os'] || 'Unknown';
            const status = meta['status'] || 'Unknown';

            html += `
                <div class="border border-gray-200 rounded-lg p-4">
                    <h4 class="font-medium text-gray-900 mb-2">${hostname}</h4>
                    <div class="space-y-1 text-sm">
                        <div><span class="text-gray-600">ID:</span> <span class="font-medium">${deviceId}</span></div>
                        <div><span class="text-gray-600">Type:</span> <span class="font-medium">${deviceType}</span></div>
                        <div><span class="text-gray-600">IP:</span> <span class="font-medium">${ipAddress}</span></div>
                        <div><span class="text-gray-600">OS:</span> <span class="font-medium">${os}</span></div>
                        <div><span class="text-gray-600">Status:</span> <span class="font-medium ${status === 'Online' ? 'text-green-600' : 'text-gray-500'}">${status}</span></div>
                    </div>
                </div>
            `;
        });

        html += `</div>`;
        return html;
    }

    /**
     * Show searching indicator
     */
    private showSearching(): void {
        const resultsContainer = document.getElementById('vector-search-results');
        if (!resultsContainer) return;

        resultsContainer.innerHTML = `
            <div class="bg-white border border-gray-200 rounded-lg p-6">
                <div class="flex items-center justify-center py-8">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                    <span class="text-gray-600">Searching vector database...</span>
                </div>
            </div>
        `;
    }

    /**
     * Show error message
     */
    private showError(message: string): void {
        const resultsContainer = document.getElementById('vector-search-results');
        if (!resultsContainer) return;

        resultsContainer.innerHTML = `
            <div class="bg-red-50 border border-red-200 rounded-lg p-6">
                <div class="flex items-center">
                    <span class="text-red-800">${message}</span>
                </div>
            </div>
        `;
    }

    /**
     * Load recent devices for page display
     */
    private async loadRecentDevices(): Promise<void> {
        if (!this.canisterClient) return;

        try {
            const result = await this.canisterClient.callCanister<DeviceTuple[]>(
                'vector_db',
                'get_all_devices',
                []
            );
            const recentDevices = result.slice(0, 5);
            this.displayRecentDevices(recentDevices);
        } catch (error) {
            console.error('Failed to load recent devices:', error);
        }
    }

    /**
     * Display recent devices section
     */
    private displayRecentDevices(devices: DeviceTuple[]): void {
        const container = document.getElementById('recent-devices');
        if (!container || !devices.length) return;

        let html = `
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 class="text-md font-medium text-gray-900 mb-3">Recent Devices</h4>
                <div class="space-y-2">
        `;

        devices.forEach(([deviceId, metadata]) => {
            const meta = this.metadataToObject(metadata);
            const deviceType = meta['device_type'] || 'unknown';
            const ipAddress = meta['ip'] || meta['ip_address'] || 'N/A';
            const hostname = meta['hostname'] || deviceId;

            html += `
                <div class="flex justify-between items-center text-sm">
                    <span class="font-medium">${hostname}</span>
                    <span class="text-gray-600">${deviceType} | ${ipAddress}</span>
                </div>
            `;
        });

        html += `
                </div>
            </div>
        `;

        container.innerHTML = html;
    }
}

// ============================================================================
// Browser Integration
// ============================================================================

declare global {
    interface Window {
        VectorSearch: typeof VectorSearch;
    }
}

window.VectorSearch = VectorSearch;
